import React, { useMemo, useState, useEffect, useRef } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { Stack, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-hot-toast';
import AuthContext from '../../shared/lib/auth/AuthContext';
// eslint-disable-next-line boundaries/element-types
import { tariffPlansResourceApi } from '../../entities/finances/tariffPlansResource/redux/tariffPlansResource.api';
// eslint-disable-next-line boundaries/element-types
import companyAccountsApi from '../../entities/companies/companyAccounts/redux/companyAccounts.api';
import { useAppDispatch } from '../store';

// eslint-disable-next-line react/prop-types
function AuthProvider({ children }) {
  const dispatch = useAppDispatch();
  const requestBalanceIntervalRef = useRef();

  const [user, setUser] = useState(undefined);
  const [insufficientBalanceToast, setInsufficientBalanceToast] = useState(false);
  const [checkBalanceTrigger, setCheckBalanceTrigger] = useState(true);

  const companyId = user?.company?.companyId;

  const isAdmin = user?.authorities.includes('ROLE_ADMIN');
  const isCompanyAdmin = user?.authorities.includes('ROLE_COMPANY_ADMIN');
  const isDealer = user?.authorities.includes('ROLE_DEALER');

  const {
    data: tariffPlans,
    refetch: refetchTariffPlans,
    isUninitialized: isTariffPlansUninitialized,
  } = tariffPlansResourceApi.useGetTariffPlansQuery(
    {},
    {
      skip: !user,
    },
  );

  const {
    data: companyAccount,
    refetch: refetchCompanyAccount,
    isUninitialized: isCompanyAccountUninitialized,
  } = companyAccountsApi.useGetCompanyAccountsQuery(
    { params: companyId ? { 'companyId.equals': companyId } : undefined },
    { skip: !(isCompanyAdmin || isAdmin) || !companyId },
  );

  const { data: balanceStatus } = companyAccountsApi.useCheckCompanyBalanceQuery(
    companyId ? { id: companyId } : skipToken,
  );

  const { currentTariffPlan, nextTariffPlan } = useMemo(() => {
    const companyAcc = companyAccount?.[0];
    const currentTarifPlanId = companyAcc?.currentPlan?.tarifPlanId;
    const nextTariffPlanId = companyAcc?.nextPlan?.tarifPlanId;
    const currentTariff = tariffPlans?.data?.find((tariff) => tariff.tarifPlanId === currentTarifPlanId) || null;
    const nextTariff = tariffPlans?.data?.find((tariff) => tariff.tarifPlanId === nextTariffPlanId) || null;
    return { currentTariffPlan: currentTariff, nextTariffPlan: nextTariff };
  }, [companyAccount, tariffPlans]);

  const checkBalance = (trigger = false) => {
    clearTimeout(requestBalanceIntervalRef.current);
    requestBalanceIntervalRef.current = setTimeout(() => {
      // Запрашиваем баланс каждые 15 минут
      dispatch(tariffPlansResourceApi.util.invalidateTags(['companyAccounts']));
      if (trigger) {
        setCheckBalanceTrigger(!checkBalanceTrigger);
      }
    }, 900000);
  };

  const showInsufficientBalanceToast = () => {
    toast.error(
      <Stack direction="row" alignItems="center">
        <Typography variant="span">Исчерпан баланс компании</Typography>
        <IconButton
          color="primary"
          size="small"
          onClick={() => {
            toast.dismiss('insufficientBalance');
            setInsufficientBalanceToast(false);
            checkBalance(true);
          }}
          sx={{ position: 'absolute', top: 0, right: 0 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>,
      { duration: Infinity, id: 'insufficientBalance' },
    );
    setInsufficientBalanceToast(true);
  };

  useEffect(() => {
    if (!isAdmin) {
      if (balanceStatus?.valid === false && !insufficientBalanceToast) {
        showInsufficientBalanceToast();
      } else if (insufficientBalanceToast) {
        toast.dismiss('insufficientBalance');
        setInsufficientBalanceToast(false);
      }

      checkBalance();
    }

    return () => {
      clearTimeout(requestBalanceIntervalRef.current);
    };
  }, [checkBalanceTrigger, balanceStatus?.valid, user?.id]);

  const signin = (newUser, callback) => {
    setUser(newUser);
    if (!isTariffPlansUninitialized) {
      refetchTariffPlans();
    }
    if (!isCompanyAccountUninitialized) {
      refetchCompanyAccount();
    }
    callback();
  };

  const signout = (callback) => {
    setUser(null);
    callback();
  };

  // eslint-disable-next-line react/jsx-no-constructed-context-values
  const value = useMemo(
    () => ({
      user: user ? { ...user, currentTariffPlan, nextTariffPlan } : undefined,
      companyAccount: companyAccount?.[0],
      companyId,
      isCompanyAdmin,
      isAdmin,
      isDealer,
      signin,
      signout,
    }),
    [user, currentTariffPlan, nextTariffPlan, companyAccount, companyId],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export default AuthProvider;
